import { useState, useEffect } from 'react';
import { X, Star, CreditCard } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// Command Deck dark theme tokens (visual only)
const FONT_DISPLAY = "'Poppins', 'Space Grotesk', sans-serif";
const FONT_BODY = "'Work Sans', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

const inputBase = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid rgba(255,255,255,.14)',
  borderRadius: 11,
  background: 'rgba(255,255,255,.04)',
  color: '#fff',
  fontFamily: FONT_BODY,
  fontSize: 13.5,
  outline: 'none',
};
const inputFocus = (e) => { e.target.style.boxShadow = '0 0 0 3px rgba(40,192,208,.12)'; e.target.style.borderColor = 'rgba(40,192,208,.5)'; };
const inputBlur = (e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(255,255,255,.14)'; };

const ReviewModal = ({ isOpen, onClose, job, crewMember, onSubmitSuccess, onComplete, isDark }) => {
  // For advanced jobs with multiple crew, crewMember is the specific crew member being reviewed
  // If crewMember is provided, use their info; otherwise fall back to job's hiredCrew/assigned_crew
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [showExtraPayment, setShowExtraPayment] = useState(false);
  const [extraAmount, setExtraAmount] = useState('');
  const [extraReason, setExtraReason] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [newCard, setNewCard] = useState({
    number: '',
    expiry: '',
    cvc: '',
    name: ''
  });
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
    }
  }, [isOpen]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/methods`,
        { params: { user_email: user.email } }
      );

      if (response.data.payment_methods) {
        setPaymentMethods(response.data.payment_methods);
        if (response.data.payment_methods.length > 0) {
          setSelectedPaymentMethod(response.data.payment_methods[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    }
  };

  // iOS-safe body scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
  const handleAddNewCard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('shootzu_token');

      if (!token) {
        toast.error('Please log in to add payment methods');
        return;
      }

      // Redirect to Stripe Checkout for secure card setup
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/setup-card`,
        {
          userEmail: user.email,
          originUrl: window.location.origin
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to add payment method:', error);
      toast.error('Failed to add payment method');
    } finally {
      setLoading(false);
    }
  };
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (showExtraPayment && extraAmount && (!extraReason.trim() || !selectedPaymentMethod)) {
      toast.error('Please complete extra payment details');
      return;
    }

    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
      const token = localStorage.getItem('shootzu_token');

      if (!token) {
        toast.error('Please log in to submit reviews');
        return;
      }

      const reviewData = {
        job_id: job.id,
        reviewer_id: user.id,
        reviewer_name: user.full_name || user.fullName,
        reviewer_email: user.email,
        rating: rating,
        comment: comment.trim(),
        extra_payment: showExtraPayment && extraAmount ? {
          amount: parseFloat(extraAmount),
          reason: extraReason.trim(),
          payment_method_id: selectedPaymentMethod
        } : null,
        // For advanced jobs: include specific crew member info for per-crew release
        crew_member: crewMember ? {
          user_id: crewMember.userId,
          name: crewMember.name,
          email: crewMember.email,
          role: crewMember.role,
          subtotal: crewMember.subtotal,
          crew_payout: crewMember.crewPayout
        } : null
      };

      // Use per-crew endpoint if crewMember is provided
      const endpoint = crewMember
        ? `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/release-payment-crew`
        : `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/release-payment`;

      const response = await axios.post(endpoint, reviewData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Review submitted and payment release initiated!');
        if (onComplete) onComplete();
        if (onSubmitSuccess) onSubmitSuccess();
        onClose();

        // Reset form
        setRating(0);
        setComment('');
        setShowExtraPayment(false);
        setExtraAmount('');
        setExtraReason('');
      }
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error('Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const crewDisplayName = crewMember?.name || job.other_party_name || job.crew_name || job.assigned_crew_name || job.hiredCrew?.name || 'Crew Member';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{
        background: 'rgba(8,11,20,.66)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        padding: 20,
        overflow: 'hidden',
        touchAction: 'none',
        fontFamily: FONT_BODY,
      }}
    >
      <div
        className="w-[95vw] h-[85vh] lg:w-full lg:max-w-2xl lg:max-h-[90vh] flex flex-col overflow-hidden"
        style={{
          background: '#12162A',
          border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
          touchAction: 'pan-y',
        }}
      >
        {/* Header band */}
        <div
          className="flex-shrink-0"
          style={{
            position: 'relative',
            padding: '22px 26px',
            background: 'linear-gradient(120deg,#572B7C,#78287C)',
            borderRadius: '22px 22px 0 0',
          }}
        >
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 34,
              height: 34,
              borderRadius: 10,
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.18)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X className="w-[17px] h-[17px]" />
          </button>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, color: '#fff', letterSpacing: '-.4px' }}>
            Rate Your Experience
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.85)', marginTop: 5 }}>
            with {crewDisplayName}
          </div>
        </div>

        <div className="p-6 flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Booking Summary */}
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              background: 'rgba(255,255,255,.04)',
              border: '1px solid rgba(255,255,255,.1)',
              borderRadius: 14,
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginTop: 0, marginBottom: 12, fontFamily: FONT_DISPLAY }}>Booking Summary</h3>
            <div className="grid grid-cols-2 gap-4" style={{ fontSize: 13 }}>
              <div>
                <span style={{ fontWeight: 600, color: 'rgba(255,255,255,.5)' }}>Job:</span>
                <p style={{ color: '#fff', margin: 0 }}>{job.title}</p>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'rgba(255,255,255,.5)' }}>Crew Member:</span>
                <p style={{ color: '#fff', fontWeight: 600, margin: 0 }}>{crewDisplayName}</p>
                {crewMember?.role && (
                  <p style={{ fontSize: 11, color: '#28C0D0', margin: 0 }}>{crewMember.role}</p>
                )}
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'rgba(255,255,255,.5)' }}>Date:</span>
                <p style={{ color: '#fff', margin: 0 }}>{formatDate(job.date || job.jobDate)}</p>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'rgba(255,255,255,.5)' }}>Hours:</span>
                <p style={{ color: '#fff', margin: 0 }}>{crewMember?.hours || job.hours || 0}h</p>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'rgba(255,255,255,.5)' }}>Rate:</span>
                <p style={{ color: '#fff', margin: 0 }}>${crewMember?.ratePerHour || job.pay_rate || job.ratePerHour || 0}/hr</p>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: 'rgba(255,255,255,.5)' }}>Location:</span>
                <p style={{ color: '#fff', margin: 0 }}>{job.location || job.location_address || 'N/A'}</p>
              </div>
              <div className="col-span-2" style={{ paddingTop: 8, borderTop: '1px solid rgba(255,255,255,.1)' }}>
                <div className="flex justify-between items-center">
                  <span style={{ fontWeight: 700, color: '#fff' }}>{crewMember ? 'Role Total:' : 'Base Total:'}</span>
                  <span style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 800, color: '#67C6B4' }}>
                    {formatCurrency(crewMember?.subtotal || job.total_amount || job.total || job.budget || 0)}
                  </span>
                </div>
                {crewMember?.crewPayout && (
                  <div className="flex justify-between items-center" style={{ fontSize: 13, marginTop: 4 }}>
                    <span style={{ color: 'rgba(255,255,255,.6)' }}>Crew Payout (after 2% fee):</span>
                    <span style={{ fontWeight: 600, color: '#28C0D0' }}>
                      {formatCurrency(crewMember.crewPayout)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Crew Member Review */}
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0, fontFamily: FONT_DISPLAY }}>
              Rate Your Experience with {crewDisplayName}
            </h4>
          </div>

          {/* Star Rating */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 12 }}>
              Rating *
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="transition-transform hover:scale-110"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <Star
                    className="w-10 h-10 transition-colors"
                    style={{
                      color: star <= (hoveredStar || rating) ? '#FCD34D' : 'rgba(255,255,255,.18)',
                      fill: star <= (hoveredStar || rating) ? '#FCD34D' : 'transparent',
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
          {/* Comment */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 8 }}>
              Comment (Optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience working with this crew member..."
              rows={4}
              style={{ ...inputBase, resize: 'none', lineHeight: 1.5 }}
              onFocus={inputFocus}
              onBlur={inputBlur}
            />
          </div>
          {/* Add Extra Payment Section */}
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              background: 'rgba(255,255,255,.04)',
              border: '1px solid rgba(255,255,255,.1)',
              borderRadius: 14,
            }}
          >
            <h4 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_DISPLAY }}>
              Additional Payment
              <span style={{ fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,.5)' }}>(Optional)</span>
            </h4>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginTop: 0, marginBottom: 16 }}>
              Add payment for parking, overtime hours, or other agreed expenses
            </p>

            {!showExtraPayment ? (
              <button
                onClick={() => setShowExtraPayment(true)}
                style={{
                  width: '100%',
                  padding: '11px',
                  borderRadius: 11,
                  background: 'rgba(255,255,255,.04)',
                  border: '1px solid rgba(255,255,255,.2)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                + Add Extra Payment
              </button>
            ) : (
              <div className="space-y-4">
                {/* Quick amount chips */}
                <div className="flex gap-2">
                  {[25, 50, 100].map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setExtraAmount(String(chip))}
                      style={{
                        flex: 1,
                        padding: 8,
                        borderRadius: 9,
                        background: String(chip) === extraAmount ? 'rgba(40,192,208,.16)' : 'rgba(255,255,255,.05)',
                        border: String(chip) === extraAmount ? '1px solid rgba(40,192,208,.5)' : '1px solid rgba(255,255,255,.14)',
                        color: String(chip) === extraAmount ? '#28C0D0' : 'rgba(255,255,255,.75)',
                        fontFamily: FONT_BODY,
                        fontWeight: 600,
                        fontSize: 12.5,
                        cursor: 'pointer',
                      }}
                    >
                      +${chip}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 8 }}>
                      Amount ($) *
                    </label>
                    <input
                      type="number"
                      value={extraAmount}
                      onChange={(e) => setExtraAmount(e.target.value)}
                      placeholder="120"
                      min="0"
                      step="0.01"
                      style={inputBase}
                      onFocus={inputFocus}
                      onBlur={inputBlur}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 8 }}>
                      Reason *
                    </label>
                    <input
                      type="text"
                      value={extraReason}
                      onChange={(e) => setExtraReason(e.target.value)}
                      placeholder="Parking"
                      style={inputBase}
                      onFocus={inputFocus}
                      onBlur={inputBlur}
                    />
                  </div>
                </div>

                {extraAmount && parseFloat(extraAmount) > 0 && (
                  <div style={{ background: 'rgba(103,198,180,.07)', border: '1px solid rgba(103,198,180,.22)', borderRadius: 13, padding: 13 }}>
                    <div style={{ fontSize: 13 }} className="space-y-1">
                      <div className="flex justify-between">
                        <span style={{ color: 'rgba(255,255,255,.65)' }}>Extra Payment:</span>
                        <span style={{ fontWeight: 600, color: '#fff' }}>{formatCurrency(extraAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'rgba(255,255,255,.65)' }}>Platform Fee (5%):</span>
                        <span style={{ fontWeight: 600, color: '#fff' }}>{formatCurrency(parseFloat(extraAmount) * 0.05)}</span>
                      </div>
                      <div className="flex justify-between" style={{ borderTop: '1px solid rgba(103,198,180,.2)', paddingTop: 6, fontSize: 15, fontWeight: 700 }}>
                        <span style={{ color: '#fff' }}>Total Payable Today:</span>
                        <span style={{ color: '#67C6B4', fontFamily: FONT_DISPLAY, fontWeight: 800 }}>{formatCurrency(parseFloat(extraAmount) * 1.05)}</span>
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(103,198,180,.2)' }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.7)', marginTop: 0, marginBottom: 8 }}>Payment Method:</p>
                      {paymentMethods.length > 0 ? (
                        <div className="space-y-2">
                          {paymentMethods.map((method) => (
                            <label
                              key={method.id}
                              className="flex items-center gap-3"
                              style={{
                                padding: 8,
                                border: selectedPaymentMethod === method.id ? '1px solid rgba(40,192,208,.5)' : '1px solid rgba(255,255,255,.14)',
                                background: selectedPaymentMethod === method.id ? 'rgba(40,192,208,.1)' : 'rgba(255,255,255,.04)',
                                borderRadius: 10,
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="radio"
                                name="payment_method"
                                value={method.id}
                                checked={selectedPaymentMethod === method.id}
                                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                style={{ width: 16, height: 16, accentColor: '#28C0D0' }}
                              />
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-4 h-4" style={{ color: 'rgba(255,255,255,.5)' }} />
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                                  {method.card?.brand || method.brand || 'Card'} •••• {method.card?.last4 || method.last4 || '****'}
                                </span>
                                {(method.card?.exp_month || method.exp_month) && (method.card?.exp_year || method.exp_year) && (
                                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', fontFamily: FONT_MONO }}>
                                    {method.card?.exp_month || method.exp_month}/{method.card?.exp_year || method.exp_year}
                                  </span>
                                )}
                              </div>
                            </label>
                          ))}

                          {/* Add New Card Option */}
                          <div style={{ marginTop: 12, padding: 12, border: '2px dashed rgba(255,255,255,.16)', borderRadius: 11 }}>
                            {!showAddCard ? (
                              <button
                                onClick={() => setShowAddCard(true)}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  borderRadius: 10,
                                  background: 'rgba(40,192,208,.08)',
                                  border: '1px solid rgba(40,192,208,.35)',
                                  color: '#28C0D0',
                                  fontWeight: 600,
                                  fontSize: 13,
                                  cursor: 'pointer',
                                }}
                              >
                                + Add New Card
                              </button>
                            ) : (
                              <div className="space-y-3">
                                <h5 style={{ fontWeight: 600, color: '#fff', margin: 0 }}>Add New Payment Method</h5>

                                <div className="space-y-3">
                                  <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 4 }}>
                                      Card Number
                                    </label>
                                    <input
                                      type="text"
                                      value={newCard.number}
                                      onChange={(e) => {
                                        let value = e.target.value.replace(/\D/g, '');
                                        value = value.replace(/(\d{4})(?=\d)/g, '$1 ');
                                        setNewCard(prev => ({ ...prev, number: value }));
                                      }}
                                      placeholder="1234 5678 9012 3456"
                                      maxLength={19}
                                      style={{ ...inputBase, fontSize: 13, fontFamily: FONT_MONO }}
                                      onFocus={inputFocus}
                                      onBlur={inputBlur}
                                    />
                                  </div>

                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 4 }}>
                                        MM/YY
                                      </label>
                                      <input
                                        type="text"
                                        value={newCard.expiry}
                                        onChange={(e) => {
                                          let value = e.target.value.replace(/\D/g, '');
                                          if (value.length >= 2) {
                                            value = value.substring(0, 2) + '/' + value.substring(2, 4);
                                          }
                                          setNewCard(prev => ({ ...prev, expiry: value }));
                                        }}
                                        placeholder="12/26"
                                        maxLength={5}
                                        style={{ ...inputBase, fontSize: 13, fontFamily: FONT_MONO }}
                                        onFocus={inputFocus}
                                        onBlur={inputBlur}
                                      />
                                    </div>

                                    <div>
                                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 4 }}>
                                        CVC
                                      </label>
                                      <input
                                        type="text"
                                        value={newCard.cvc}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, '');
                                          setNewCard(prev => ({ ...prev, cvc: value }));
                                        }}
                                        placeholder="123"
                                        maxLength={4}
                                        style={{ ...inputBase, fontSize: 13, fontFamily: FONT_MONO }}
                                        onFocus={inputFocus}
                                        onBlur={inputBlur}
                                      />
                                    </div>
                                  </div>

                                  <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.6)', marginBottom: 4 }}>
                                      Cardholder Name
                                    </label>
                                    <input
                                      type="text"
                                      value={newCard.name}
                                      onChange={(e) => setNewCard(prev => ({ ...prev, name: e.target.value }))}
                                      placeholder={user.full_name || user.fullName || 'Full Name'}
                                      style={{ ...inputBase, fontSize: 13 }}
                                      onFocus={inputFocus}
                                      onBlur={inputBlur}
                                    />
                                  </div>
                                </div>

                                <div className="flex gap-2" style={{ paddingTop: 12 }}>
                                  <button
                                    onClick={() => setShowAddCard(false)}
                                    style={{
                                      flex: 1,
                                      padding: '10px',
                                      borderRadius: 10,
                                      background: 'transparent',
                                      border: '1px solid rgba(255,255,255,.2)',
                                      color: '#fff',
                                      fontWeight: 600,
                                      fontSize: 13,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleAddNewCard();
                                    }}
                                    disabled={loading}
                                    style={{
                                      flex: 1,
                                      padding: '10px',
                                      borderRadius: 10,
                                      background: '#28C0D0',
                                      border: 'none',
                                      color: '#0C1120',
                                      fontWeight: 700,
                                      fontSize: 13,
                                      cursor: loading ? 'not-allowed' : 'pointer',
                                      opacity: loading ? 0.6 : 1,
                                    }}
                                  >
                                    {loading ? 'Adding...' : 'Add Card'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: 16, background: 'rgba(185,28,28,.16)', border: '1px solid rgba(248,113,113,.45)', borderRadius: 11 }}>
                          <p style={{ color: '#F87171', fontSize: 13, fontWeight: 600, marginTop: 0, marginBottom: 8 }}>No payment methods found</p>
                          <p style={{ color: 'rgba(248,113,113,.8)', fontSize: 11, marginTop: 0, marginBottom: 12 }}>You need to add a payment method first</p>
                          <button
                            onClick={() => {
                              window.open('/settings', '_blank');
                            }}
                            style={{
                              background: '#28C0D0',
                              color: '#0C1120',
                              borderRadius: 9,
                              border: 'none',
                              fontWeight: 700,
                              fontSize: 12,
                              padding: '8px 16px',
                              cursor: 'pointer',
                            }}
                          >
                            Go to Settings
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowExtraPayment(false);
                      setExtraAmount('');
                      setExtraReason('');
                    }}
                    style={{
                      padding: '9px 16px',
                      borderRadius: 10,
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,.2)',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: '13px',
                borderRadius: 12,
                background: 'transparent',
                border: '1px solid rgba(255,255,255,.2)',
                color: '#fff',
                fontWeight: 600,
                fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || rating === 0}
              style={{
                flex: 1.6,
                padding: '13px',
                borderRadius: 12,
                background: '#15803D',
                border: 'none',
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                cursor: loading || rating === 0 ? 'not-allowed' : 'pointer',
                opacity: loading || rating === 0 ? 0.6 : 1,
              }}
            >
              {loading ? 'Submitting...' : 'Submit Review & Release Payment'}
            </button>
          </div>
        </div>
        <p className="text-[8px] text-center py-1 opacity-20 select-none" style={{ color: '#fff' }}>modal:review</p>
      </div>
    </div>
  );
};

export default ReviewModal;
